import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { thaiDate } from '../lib/format'

/** Row shape from GET /v1/licenses. `signature` is the complete AAI-LIC1 token. */
interface LicenseRow {
  id: string
  customer_name: string
  seats: number
  features: string[]
  issued_at: string
  expires_at: string
  signature: string
  public_key_id: string
  revoked_at?: string
}

interface LicenseVerification {
  valid: boolean
  reason: string
  key_id?: string
  customer?: string
  seats?: number
  features?: string[]
  issued_at?: string
  expires_at?: string
  revoked_at?: string
}

const licenseQueryKey = ['licenses'] as const

const FEATURES: { id: string; label: string; hint: string }[] = [
  { id: 'sso', label: 'SSO', hint: 'เข้าสู่ระบบผ่านผู้ให้บริการตัวตนขององค์กร' },
  { id: 'audit_trail', label: 'ร่องรอยการตรวจสอบ', hint: 'บันทึกและตรวจสอบร่องรอยแบบลูกโซ่แฮช' },
  { id: 'whistleblower', label: 'แจ้งเบาะแส', hint: 'ช่องทางแจ้งเบาะแสแบบไม่ระบุตัวตน' },
  { id: 'trust_center', label: 'ศูนย์ความโปร่งใส', hint: 'หน้าเผยแพร่ข้อมูลความโปร่งใสสู่สาธารณะ' },
  { id: 'api_access', label: 'API ภายนอก', hint: 'เชื่อมต่อระบบภายนอกผ่านคีย์ API' },
  { id: 'aml', label: 'AML / STR', hint: 'ตรวจธุรกรรมต้องสงสัยและรายงาน STR' },
  { id: 'erp', label: 'เชื่อมต่อ ERP', hint: 'นำเข้าข้อมูลจากระบบ ERP' },
  { id: 'payroll', label: 'เงินเดือน', hint: 'ตรวจทะเบียนเงินเดือน' },
  { id: 'inventory', label: 'สินค้าคงคลัง', hint: 'ตรวจนับและวิเคราะห์สินค้าขาด' },
  { id: 'forensics', label: 'นิติวิทยาศาสตร์', hint: 'วิเคราะห์เครือข่ายคู่ค้า' },
  { id: 'masking', label: 'ปิดข้อมูล', hint: 'ปกปิดข้อมูลส่วนบุคคลตามนโยบาย' },
  { id: 'offline', label: 'ใช้งานออฟไลน์', hint: 'ติดตั้งในศูนย์ข้อมูลของลูกค้าโดยไม่ต่ออินเทอร์เน็ต' },
]

const featureLabel = (id: string) => FEATURES.find((f) => f.id === id)?.label ?? id

const reasonLabel: Record<string, string> = {
  ok: 'ลายเซ็นถูกต้องและยังไม่หมดอายุ',
  malformed: 'รูปแบบไลเซนส์ไม่ถูกต้อง ต้องขึ้นต้นด้วย AAI-LIC1',
  unknown_key: 'ไม่พบกุญแจสาธารณะที่ใช้ลงนามไลเซนส์นี้',
  bad_signature: 'ลายเซ็นไม่ตรงกับเนื้อหา ไลเซนส์อาจถูกแก้ไข',
  invalid_payload: 'เนื้อหาไลเซนส์ไม่ถูกต้อง',
  expired: 'ไลเซนส์หมดอายุแล้ว',
  revoked: 'ไลเซนส์ถูกเพิกถอนแล้ว',
}

function defaultExpiry(): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() + 1)
  return d.toISOString().slice(0, 10)
}

function shortToken(token: string): string {
  return token.length > 30 ? `${token.slice(0, 30)}…` : token
}

function rowStatus(row: LicenseRow): { label: string; cls: string } {
  if (row.revoked_at) return { label: 'เพิกถอนแล้ว', cls: '' }
  if (new Date(row.expires_at).getTime() <= Date.now()) return { label: 'หมดอายุ', cls: '' }
  return { label: 'ใช้งานได้', cls: ' gold' }
}

export function Licenses() {
  const qc = useQueryClient()
  const licenses = useQuery({
    queryKey: licenseQueryKey,
    queryFn: () => api<LicenseRow[]>('/v1/licenses'),
    staleTime: 15_000,
  })

  const [customer, setCustomer] = useState('')
  const [seats, setSeats] = useState('10')
  const [features, setFeatures] = useState<string[]>(['audit_trail'])
  const [expiresAt, setExpiresAt] = useState(defaultExpiry)
  const [issued, setIssued] = useState<LicenseRow | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const [paste, setPaste] = useState('')
  const [verification, setVerification] = useState<LicenseVerification | null>(null)

  const issue = useMutation({
    mutationFn: (input: { customer: string; seats: number; features: string[]; expires_at: string }) =>
      apiWrite<{ license: LicenseRow }>('/v1/licenses', 'POST', input),
    onSuccess: (res) => {
      setIssued(res.license)
      setCopied(false)
      void qc.invalidateQueries({ queryKey: licenseQueryKey })
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => apiWrite<LicenseRow>(`/v1/licenses/${id}/revoke`, 'POST', {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: licenseQueryKey }) },
    onSettled: () => setConfirmId(null),
  })

  const verify = useMutation({
    mutationFn: (token: string) => api<LicenseVerification>('/v1/licenses/verify', {
      method: 'POST',
      body: JSON.stringify({ license: token }),
    }),
    onSuccess: (res) => setVerification(res),
    onError: () => setVerification(null),
  })

  const toggleFeature = (id: string) => {
    setFeatures((cur) => cur.includes(id) ? cur.filter((f) => f !== id) : [...cur, id])
  }

  const copyToken = async () => {
    if (!issued) return
    try {
      await navigator.clipboard.writeText(issued.signature)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const rows = licenses.data ?? []
  const seatCount = Number(seats)
  const canIssue = customer.trim().length > 0 && Number.isFinite(seatCount) && seatCount > 0 && expiresAt.length === 10

  return (
    <>
      <PageHead title="ไลเซนส์สำหรับติดตั้งภายใน"
        sub="ออกไลเซนส์ที่ลงนามด้วย ed25519 สำหรับลูกค้าที่ติดตั้งระบบในศูนย์ข้อมูลของตนเอง" />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">ออกไลเซนส์ใหม่</div>

          <div className="field">
            <label htmlFor="lic-customer">ชื่อลูกค้า</label>
            <input id="lic-customer" className="input" maxLength={120}
              placeholder="เช่น บริษัท ตัวอย่าง จำกัด (มหาชน)"
              value={customer} onChange={(e) => setCustomer(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="lic-seats">จำนวนที่นั่ง</label>
            <input id="lic-seats" className="input tnum" type="number" min={1} max={100000}
              value={seats} onChange={(e) => setSeats(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="lic-expiry">วันหมดอายุ</label>
            <input id="lic-expiry" className="input" type="date"
              value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.7 }}>
              ใช้ได้ถึงสิ้นวันตามเวลาประเทศไทย (UTC+7)
            </div>
          </div>

          <div className="field">
            <label>ฟีเจอร์ที่เปิดใช้</label>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {FEATURES.map((f) => (
                <button key={f.id} type="button" title={f.hint}
                  className={`chip${features.includes(f.id) ? ' chip-on' : ''}`}
                  onClick={() => toggleFeature(f.id)}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.7 }}>
              {features.length === 0
                ? 'ไม่ได้เลือกฟีเจอร์เพิ่มเติม — ไลเซนส์นี้เปิดเฉพาะผลิตภัณฑ์พื้นฐาน'
                : FEATURES.filter((f) => features.includes(f.id)).map((f) => `${f.label} — ${f.hint}`).join(' · ')}
            </div>
          </div>

          {issue.isError ? (
            <div className="form-error">
              {issue.error instanceof Error ? issue.error.message : 'ออกไลเซนส์ไม่สำเร็จ'}
            </div>
          ) : null}

          <button className="btn btn-primary" style={{ marginTop: 18 }} disabled={!canIssue || issue.isPending}
            onClick={() => issue.mutate({
              customer: customer.trim(),
              seats: seatCount,
              features,
              expires_at: expiresAt,
            })}>
            {issue.isPending ? 'กำลังลงนาม…' : 'ออกไลเซนส์'}
          </button>

          {issued ? (
            <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--color-hairline)' }}>
              <div className="row">
                <span className="card-label" style={{ margin: 0 }}>ไลเซนส์ที่ออกแล้ว</span>
                <span className="chip gold" style={{ marginLeft: 'auto' }}>{issued.customer_name}</span>
              </div>
              <p className="dim" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.7 }}>
                ส่งข้อความนี้ให้ลูกค้านำไปวางในหน้าตรวจสอบของระบบที่ติดตั้งภายใน
                ลายเซ็นผูกกับเนื้อหาทั้งหมด หากแก้ไขแม้แต่ตัวอักษรเดียวจะตรวจสอบไม่ผ่าน
              </p>
              <textarea readOnly className="input mono" aria-label="ไลเซนส์ที่ออกใหม่"
                style={{ width: '100%', height: 110, padding: 10, marginTop: 8, resize: 'vertical' }}
                value={issued.signature} />
              <div className="row" style={{ marginTop: 10, gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={() => void copyToken()}>
                  {copied ? 'คัดลอกแล้ว' : 'คัดลอกไลเซนส์'}
                </button>
                <span className="dim mono" style={{ fontSize: 11 }}>{issued.public_key_id}</span>
                <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                  onClick={() => setIssued(null)}>ปิด</button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">ตรวจสอบไลเซนส์</div>
          <p className="dim" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.7 }}>
            วางไลเซนส์ที่ได้รับเพื่อตรวจลายเซ็น วันหมดอายุ และสถานะการเพิกถอน
            การตรวจสอบทำในเครื่องนี้ ไม่ต้องต่ออินเทอร์เน็ต
          </p>
          <div className="field">
            <label htmlFor="lic-paste">ไลเซนส์</label>
            <textarea id="lic-paste" className="input mono" placeholder="AAI-LIC1.…"
              style={{ width: '100%', height: 120, padding: 10, resize: 'vertical' }}
              value={paste} onChange={(e) => { setPaste(e.target.value); setVerification(null) }} />
          </div>
          <button className="btn" style={{ marginTop: 14 }}
            disabled={paste.trim().length === 0 || verify.isPending}
            onClick={() => verify.mutate(paste.trim())}>
            {verify.isPending ? 'กำลังตรวจสอบ…' : 'ตรวจสอบ'}
          </button>

          {verify.isError ? (
            <div className="form-error">
              {verify.error instanceof Error ? verify.error.message : 'ตรวจสอบไม่สำเร็จ'}
            </div>
          ) : null}

          {verification ? (
            <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--color-hairline)' }}>
              <span className={`chip${verification.valid ? ' gold' : ''}`}>
                {verification.valid ? 'ไลเซนส์ถูกต้อง' : 'ไลเซนส์ไม่ผ่าน'}
              </span>
              <div style={{ fontSize: 13, marginTop: 12, color: verification.valid ? 'var(--color-gold)' : '#E3A0A9' }}>
                {reasonLabel[verification.reason] ?? verification.reason}
              </div>
              {verification.customer ? (
                <div className="dim" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.9 }}>
                  <div>ลูกค้า: {verification.customer}</div>
                  <div>ที่นั่ง: {verification.seats}</div>
                  <div>ฟีเจอร์: {verification.features?.length ? verification.features.map(featureLabel).join(', ') : 'พื้นฐาน'}</div>
                  {verification.issued_at ? <div>ออกเมื่อ: {thaiDate(verification.issued_at)}</div> : null}
                  {verification.expires_at ? <div>หมดอายุ: {thaiDate(verification.expires_at)}</div> : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="card-label">ไลเซนส์ทั้งหมด</span>
          <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} รายการ</span>
        </div>

        {licenses.isLoading ? <Skel h={180} /> : rows.length === 0 ? (
          <div className="card card-pad dim" style={{ fontSize: 12.5 }}>
            ยังไม่มีไลเซนส์ — กรอกชื่อลูกค้าและจำนวนที่นั่ง แล้วกด “ออกไลเซนส์” เพื่อเริ่มต้น
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>ลูกค้า</th><th>ที่นั่ง</th><th>ฟีเจอร์</th><th>ลายเซ็น</th>
                  <th>ออกเมื่อ</th><th>หมดอายุ</th><th>สถานะ</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const status = rowStatus(row)
                  return (
                    <tr key={row.id} style={{ cursor: 'default' }}>
                      <td>{row.customer_name}</td>
                      <td className="num">{row.seats}</td>
                      <td>
                        <span className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
                          {row.features.length === 0
                            ? <span className="dim">พื้นฐาน</span>
                            : row.features.map((f) => <span key={f} className="chip">{featureLabel(f)}</span>)}
                        </span>
                      </td>
                      <td className="mono" title={row.signature}>{shortToken(row.signature)}</td>
                      <td className="dim">{thaiDate(row.issued_at)}</td>
                      <td className="dim">{thaiDate(row.expires_at)}</td>
                      <td><span className={`chip${status.cls}`}>{status.label}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        {row.revoked_at ? <span className="dim">—</span> : confirmId === row.id ? (
                          <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button className="btn btn-sm" disabled={revoke.isPending}
                              style={{ color: '#E3A0A9' }}
                              onClick={() => revoke.mutate(row.id)}>
                              {revoke.isPending ? 'กำลังเพิกถอน…' : 'ยืนยัน'}
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmId(null)}>ยกเลิก</button>
                          </span>
                        ) : (
                          <button className="btn btn-ghost btn-sm" onClick={() => setConfirmId(row.id)}>เพิกถอน</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {revoke.isError ? (
          <div className="form-error">
            {revoke.error instanceof Error ? revoke.error.message : 'เพิกถอนไม่สำเร็จ'}
          </div>
        ) : null}
      </div>
    </>
  )
}
